
import Vue from 'vue'
import { logService, DeviceDetails } from '@/service/log.service'
import LogDetailsDialog from '@/components/logs/LogDetailsDialog.vue'
import DeviceDetailsDialog from '@/components/logs/DeviceDetailsDialog.vue'

interface LogEntry {
  id: number;
  topic: string;
  subtopic: string | null;
  logLevel: string;
  createdAt: string;
  endpoint: string | null;
  requestMethod: string | null;
  statusCode: number | null;
  responseTimeMs: number | null;
  log: string;
  payload: Record<string, unknown> | null;
  userId: number | null;
  organizationId: number | null;
  roleId: number | null;
  ipAddress: string | null;
  userAgent: string | null;
  correlationId: string | null;
}

interface LogStats {
  assetType: string;
  totalLogs: number;
  logLevelCounts: { [key: string]: number };
  topicCounts: { [key: string]: number };
}

export default Vue.extend({
  name: 'Logs',
  components: {
    LogDetailsDialog,
    DeviceDetailsDialog
  },
  data () {
    return {
      loading: false,
      logs: [] as LogEntry[],
      totalLogs: 0,
      stats: null as LogStats | null,
      selectedLog: null as LogEntry | null,
      detailsDialog: false,
      deviceDetailsDialog: false,
      deviceDetails: null as DeviceDetails | null,
      loadingDeviceDetails: false,
      startDateMenu: false,
      endDateMenu: false,
      showAdvancedFilters: false,
      mainTab: 0,
      options: {
        page: 1,
        itemsPerPage: 25,
        sortBy: ['created_at'],
        sortDesc: [true]
      },
      filters: {
        assetType: 'general',
        logLevel: null as string | null,
        topic: null as string | null,
        search: null as string | null,
        userId: null as number | null,
        organizationId: null as number | null,
        clientId: null as string | null,
        startDate: null as string | null,
        endDate: null as string | null,
        endpoint: null as string | null
      },
      // App (wsgi.py) asset types
      appAssetTypes: [
        { text: 'General', value: 'general' },
        { text: 'User', value: 'user' },
        { text: 'User Activity', value: 'user_logs' },
        { text: 'Role', value: 'role' },
        { text: 'Device', value: 'device' },
        { text: 'Camera', value: 'camera' },
        { text: 'Job', value: 'job' },
        { text: 'Model', value: 'model' },
        { text: 'Pipeline', value: 'pipeline' },
        { text: 'Engine', value: 'engine' },
        { text: 'Licence', value: 'licence' },
        { text: 'Container', value: 'container' },
        { text: 'Record', value: 'record' },
        { text: 'Network', value: 'network' },
        { text: 'Schedule', value: 'schedule' },
        { text: 'Analytics', value: 'analytics' },
        { text: 'Inference Alarm', value: 'inference_alarm' },
        { text: 'Device Alarm', value: 'device_alarm' },
        { text: 'Notification Channel', value: 'notification_channel' },
        { text: 'Private Repository', value: 'private_repository' }
      ],
      // API (wsgi_api.py) asset types
      apiAssetTypes: [
        { text: 'General', value: 'api' },
        { text: 'Application', value: 'api_application' },
        { text: 'Authorization', value: 'api_authorization' },
        { text: 'Cameras', value: 'api_cameras' },
        { text: 'Client Parameters', value: 'api_client_parameters' },
        { text: 'Containers', value: 'api_containers' },
        { text: 'Cordatus Update', value: 'api_cordatus_update' },
        { text: 'Cossa', value: 'api_cossa' },
        { text: 'Data Usage', value: 'api_data_usage' },
        { text: 'Device Management', value: 'api_device_management' },
        { text: 'Docker Stream', value: 'api_docker_stream' },
        { text: 'Harbor', value: 'api_harbor' },
        { text: 'Health Check', value: 'api_health_check' },
        { text: 'Jobs', value: 'api_jobs' },
        { text: 'Licence Management', value: 'api_licence_management' },
        { text: 'Notify', value: 'api_notify' },
        { text: 'ONVIF Discovery', value: 'api_onvif_discovery' },
        { text: 'Records', value: 'api_records' },
        { text: 'Remote', value: 'api_remote' },
        { text: 'Token Management', value: 'api_token_management' }
      ],
      logLevels: ['DEBUG', 'INFO', 'WARNING', 'ERROR', 'CRITICAL'],
      devices: [] as Array<{ text: string; value: string }>,
      loadingDevices: false,
      headers: [
        { text: 'ID', value: 'id', width: '80px' },
        { text: 'Level', value: 'log_level', width: '100px' },
        { text: 'Topic', value: 'topic', width: '120px' },
        { text: 'Message', value: 'log' },
        { text: 'Endpoint', value: 'endpoint', width: '150px' },
        { text: 'Status', value: 'status_code', width: '80px' },
        { text: 'Time (ms)', value: 'response_time_ms', width: '100px' },
        { text: 'User', value: 'user_id', width: '80px' },
        { text: 'Client', value: 'client_id', width: '100px' },
        { text: 'Date', value: 'created_at', width: '180px' },
        { text: 'Actions', value: 'actions', sortable: false, width: '80px' }
      ]
    }
  },
  watch: {
    mainTab () {
      // Reset asset type when switching tabs
      this.filters.assetType = this.mainTab === 0 ? 'general' : 'api'
      this.filters.clientId = null
      this.fetchLogs()
      this.fetchDevices()
    },
    'filters.assetType' () {
      // Refresh devices when asset type changes
      this.filters.clientId = null
      this.fetchDevices()
    }
  },
  computed: {
    currentAssetTypes (): Array<{ text: string; value: string }> {
      return this.mainTab === 0 ? this.appAssetTypes : this.apiAssetTypes
    },
    hasActiveFilters (): boolean {
      return !!(
        this.filters.logLevel ||
        this.filters.topic ||
        this.filters.userId ||
        this.filters.organizationId ||
        this.filters.clientId ||
        this.filters.startDate ||
        this.filters.endDate ||
        this.filters.endpoint
      )
    },
    activeFilterCount (): number {
      return Object.entries(this.filters).filter(
        ([key, value]) => key !== 'assetType' && value !== null && value !== ''
      ).length
    }
  },
  mounted () {
    this.fetchLogs()
    this.fetchDevices()
  },
  methods: {
    onAssetTypeChange () {
      // Clear device selection when asset type changes
      this.filters.clientId = null
      this.devices = []
      // Fetch devices for new asset type
      if (this.filters.assetType && this.mainTab === 0) {
        this.fetchDevices()
      }
      this.fetchLogs()
    },
    async fetchDevices () {
      if (this.loadingDevices || !this.filters.assetType) return
      this.loadingDevices = true
      try {
        const assetType = this.filters.assetType || 'general'
        const response = await logService.getDevices(assetType)
        // Ensure response is an array and filter out invalid entries
        const deviceList = Array.isArray(response) ? response : []
        this.devices = deviceList
          .filter((d) => d && d.client_id)
          .map((d) => ({
            text: `${d.client_id.substring(0, 16)}... (${
              d.log_count || 0
            } logs)`,
            value: d.client_id
          }))
      } catch (error) {
        console.error('Failed to fetch devices:', error)
        this.devices = []
      } finally {
        this.loadingDevices = false
      }
    },
    async fetchLogs () {
      if (!this.filters.assetType) {
        this.filters.assetType = 'general'
      }

      this.loading = true
      try {
        const params: Record<string, unknown> = {
          assetType: this.filters.assetType,
          page: this.options.page,
          pageSize: this.options.itemsPerPage,
          orderBy: this.options.sortBy[0] || 'created_at',
          orderDir: this.options.sortDesc[0] ? 'desc' : 'asc'
        }

        if (this.filters.logLevel) params.logLevel = this.filters.logLevel
        if (this.filters.topic) params.topic = this.filters.topic
        if (this.filters.search) params.search = this.filters.search
        if (this.filters.userId) params.userId = this.filters.userId
        if (this.filters.organizationId) {
          params.organizationId = this.filters.organizationId
        }
        if (this.filters.clientId) params.clientId = this.filters.clientId
        if (this.filters.startDate) {
          params.startDate = this.filters.startDate + 'T00:00:00'
        }
        if (this.filters.endDate) {
          params.endDate = this.filters.endDate + 'T23:59:59'
        }

        const response = await logService.getLogs(params)
        this.logs = response.items || response.logs || []
        this.totalLogs = response.total || this.logs.length

        // Fetch stats
        await this.fetchStats()
      } catch (error) {
        console.error('Failed to fetch logs:', error)
        this.$store.dispatch('showSnackbar', {
          text: 'Failed to fetch logs',
          color: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async fetchStats () {
      try {
        const params: Record<string, unknown> = {
          assetType: this.filters.assetType || 'general'
        }
        if (this.filters.startDate) {
          params.startDate = this.filters.startDate + 'T00:00:00'
        }
        if (this.filters.endDate) {
          params.endDate = this.filters.endDate + 'T23:59:59'
        }

        const response = await logService.getStats(params)
        this.stats = response
      } catch (error) {
        console.error('Failed to fetch stats:', error)
      }
    },
    clearFilters () {
      this.filters = {
        assetType: 'general',
        logLevel: null,
        topic: null,
        search: null,
        userId: null,
        organizationId: null,
        startDate: null,
        endDate: null,
        endpoint: null,
        clientId: null
      }
      this.fetchLogs()
    },
    showLogDetails (log: LogEntry) {
      this.selectedLog = log
      this.detailsDialog = true
    },
    formatDate (dateStr: string): string {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      // Use user's browser locale and timezone
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      })
    },
    getLogLevelColor (level: string): string {
      const colors: { [key: string]: string } = {
        DEBUG: 'grey',
        INFO: 'blue',
        WARNING: 'orange',
        ERROR: 'red',
        CRITICAL: 'deep-purple'
      }
      return colors[level] || 'grey'
    },
    getStatusCodeColor (code: number): string {
      if (code >= 200 && code < 300) return 'success'
      if (code >= 300 && code < 400) return 'info'
      if (code >= 400 && code < 500) return 'warning'
      if (code >= 500) return 'error'
      return 'grey'
    },
    async showDeviceDetails (clientId: string) {
      if (!clientId) return
      this.deviceDetailsDialog = true
      this.loadingDeviceDetails = true
      this.deviceDetails = null

      try {
        const details = await logService.getDeviceDetails(clientId)
        this.deviceDetails = details
      } catch (error) {
        console.error('Failed to fetch device details:', error)
        this.$store.dispatch('showSnackbar', {
          text: 'Failed to fetch device details',
          color: 'error'
        })
      } finally {
        this.loadingDeviceDetails = false
      }
    }
  }
})
